import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { ASSETS, NAV, LINKS, HOURS, ADDRESS, LISTINGS } from "../lib/constants";
import { subscribe } from "../lib/api";

const FooterSubscribe = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribe({ email, interest: "Newsletter" });
      setDone(true);
      setEmail("");
      toast.success("You're subscribed. Thanks for joining our list.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-10 max-w-md" data-testid="footer-subscribe">
      <p className="text-xs uppercase tracking-[0.25em] text-gold">Join Our Email List</p>
      <p className="mt-3 text-sm leading-relaxed text-white/55">
        New drops, deals, and events at Higher Ground DC — straight to your inbox.
      </p>
      {done ? (
        <div className="mt-5 flex items-center gap-3 text-sm text-white/70" data-testid="footer-subscribe-success">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gold/50 bg-gold/10">
            <Check className="h-4 w-4 text-gold" />
          </span>
          You're on the list.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            data-testid="footer-subscribe-input"
            className="w-full border border-white/15 bg-ink-2 px-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-gold"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="footer-subscribe-submit"
            className="inline-flex flex-shrink-0 items-center justify-center gap-2 bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Subscribe <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
      )}
    </div>
  );
};

export const Footer = () => (
  <footer className="border-t border-white/10 bg-ink" data-testid="footer">
    <div className="mx-auto max-w-[1400px] px-6 py-16 lg:px-12 lg:py-24">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div className="lg:col-span-2">
          <img src={ASSETS.logo} alt="Higher Ground DC" className="h-16 w-auto object-contain" />
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/55">
            Washington, DC's premier patient-first medical cannabis destination near Union Market —
            where wellness, education, and lifestyle come together.
          </p>
          <div className="mt-8 space-y-3 text-sm text-white/60">
            <a href={LINKS.google} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 transition-colors hover:text-gold" data-testid="footer-map">
              <MapPin className="h-4 w-4 text-gold" /> {ADDRESS.line1}, {ADDRESS.line2}
            </a>
            <a href={`tel:${LINKS.phone}`} className="flex items-center gap-3 transition-colors hover:text-gold" data-testid="footer-phone">
              <Phone className="h-4 w-4 text-gold" /> (202) 800-1135
            </a>
            <span className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-gold" /> {HOURS}
            </span>
          </div>
          <FooterSubscribe />
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Explore</p>
          <ul className="mt-6 space-y-3">
            {NAV.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className="text-sm text-white/60 transition-colors hover:text-white" data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Patients</p>
          <ul className="mt-6 space-y-3">
            <li><a href={LINKS.menu} target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 transition-colors hover:text-white">Live Menu</a></li>
            <li><a href={LINKS.registerDC} target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 transition-colors hover:text-white">Register — DC Resident</a></li>
            <li><a href={LINKS.registerNonDC} target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 transition-colors hover:text-white">Register — Non-Resident</a></li>
            <li><Link to="/waiver" data-testid="footer-waiver-link" className="text-sm text-white/60 transition-colors hover:text-white">Safe Use Waiver</Link></li>
            <li><Link to="/signup" className="text-sm text-white/60 transition-colors hover:text-white">Sign Up</Link></li>
          </ul>
        </div>
      </div>

      {/* Trusted listings & mentions (backlinks / citations) */}
      <div className="mt-16 border-t border-white/10 pt-12" data-testid="trusted-listings">
        <p className="text-xs uppercase tracking-[0.25em] text-gold">Trusted Listings & Mentions</p>
        <h3 className="mt-3 font-display text-xl font-light uppercase tracking-[0.06em] text-white md:text-2xl">
          Find Higher Ground DC Across Official, Local & Media Sources
        </h3>
        <p className="mt-3 max-w-2xl text-sm text-white/55">
          Explore Higher Ground DC across official listings, directories, local maps, social updates, and media mentions.
        </p>
        <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
          {LISTINGS.map((group) => (
            <div key={group.category}>
              <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{group.category}</p>
              <ul className="mt-4 space-y-2.5">
                {group.items.map((it, i) => (
                  <li key={it.label + i}>
                    <a
                      href={it.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`listing-${group.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`}
                      className="text-sm text-white/60 transition-colors hover:text-gold"
                    >
                      {it.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row md:items-center">
        <p>© {new Date().getFullYear()} Higher Ground DC Cannabis. All rights reserved. SEO &amp; website managed by{" "}
          <a href="https://webnehtech.com" target="_blank" rel="noopener noreferrer" className="text-white/60 transition-colors hover:text-gold" data-testid="footer-webnehtech">webnehtech.com</a>.
        </p>
        <p className="max-w-xl md:text-right">
          Must be 21+ to enter. Medical cannabis products are for registered patients only. Keep out of reach of children.
        </p>
      </div>
    </div>
  </footer>
);
